// src/components/proposals/ProposalKanban.jsx
//
// ⚠ NAMED ProposalKanban, NOT ProposalBoard. proposalBoard.js (the React-free
// rules) sits beside this file, and on a case-insensitive filesystem an import
// of "./ProposalBoard" resolves to it first (webpack tries .js before .jsx) —
// the build then fails with "does not contain a default export". The quote
// board paid for this once already.
//
// The proposals kanban: Drafts · Sent · Won · Contract Sent & Signed ·
// Contracts & Projects In Autotask. The rules — which column, which chips —
// live in proposalBoard.js (React-free, tested); this file only draws them.
//
// ⛔ NOTHING ON THIS BOARD IS DRAGGABLE, unlike the quote board, and the reason
// is that no column is a reversible flag: Draft → Sent has a real client send
// behind it, Won is reconciled from a real client acceptance, and the Contract
// and Autotask columns are DERIVED from other records entirely — a proposal
// cannot be dragged into "has a signed contract" or "has an Autotask contract"
// without lying. Status changes stay exactly where they are today (the opened
// proposal's status select). Do not add drag here by analogy with quoting.
//
// ⚠ LAYOUT: five columns plus horizontal scroll. The board root carries
// `minWidth: 0` and the column tracks are fixed-width flex items that never
// shrink below their content — the shrink-chain trap (1fr is minmax(auto,1fr),
// min-width:auto on every flex/grid item) is what made the client register and
// the quote start modal go wide. Same recipe as QuoteKanban.
import React from "react";
import {
  BOARD_COLUMNS, COLUMN_LABELS, groupByColumn, contractChipFor, autotaskChipFor,
} from "./proposalBoard";

const chipStyle = (c) => ({
  fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: c.bg, color: c.fg,
  textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
});

/**
 * @param proposals          every proposal; leads, lost and archived are left
 *                           off by columnFor and never reach a column
 * @param contractInstances  the contract records the columns are derived from
 * @param renderCard         (proposal) => the existing ProposalCard — reused,
 *                           not re-drawn, so the two views cannot drift
 */
export default function ProposalKanban({ proposals, contractInstances = [], renderCard }) {
  const cols = groupByColumn(proposals, contractInstances);
  const byRecent = (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0);

  return (
    <div data-testid="proposal-board"
      style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", padding: "4px 0 14px", alignItems: "stretch" }}>
      {BOARD_COLUMNS.map((col) => (
        <div key={col} data-testid={`column-${col}`}
          style={{
            width: 320, minWidth: 320, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0,
            background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, overflowY: "auto",
          }}>
          <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "8px 12px", background: "white", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={COLUMN_LABELS[col]}>{COLUMN_LABELS[col]}</span>
            <span data-testid={`count-${col}`} style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", background: "var(--s2)", padding: "1px 7px", borderRadius: 10, flexShrink: 0 }}>{cols[col].length}</span>
          </div>
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
            {[...cols[col]].sort(byRecent).map((p) => {
              const contract = contractChipFor(p, contractInstances);
              const autotask = autotaskChipFor(p, contractInstances);
              return (
                <div key={p.id} data-testid={`board-card-${p.id}`} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  {/* The derived state the card itself does not know about: the
                      contract instance and the Autotask record. The status chip
                      and the P number are already on the card. A declined or
                      voided contract keeps its chip even though it does not
                      advance the column — that is exactly the card worth spotting. */}
                  {(contract || autotask) && (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", padding: "0 2px" }}>
                      {contract && (
                        <span data-testid={`chip-contract-${p.id}`} style={chipStyle(contract)}
                              title={contract.count > 1 ? `${contract.count} contract instances — showing the furthest` : undefined}>
                          {contract.label}
                        </span>
                      )}
                      {autotask && (
                        <span data-testid={`chip-autotask-${p.id}`} style={chipStyle(autotask)}>Autotask · {autotask.label}</span>
                      )}
                    </div>
                  )}
                  {renderCard(p)}
                </div>
              );
            })}
            {cols[col].length === 0 && (
              <div style={{ fontSize: 10, color: "var(--text3)", fontStyle: "italic", textAlign: "center", padding: "18px 6px" }}>—</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
